import ActionButton from "@/components/Button/ActionButton";
import { Checkbox } from "@/components/Checkbox";
import { HStack, VStack } from "@/components/Layout";
import { Loader } from "@/components/Loading/Loader";
import { Text } from "@/components/Typography";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import { cn } from "@/lib/cn";
import { Avatar, AvatarImage } from "@radix-ui/react-avatar";
import { useMemo, useRef } from "react";
import SelectedOptionChip from "./SelectedOptionChip";
import type {
  ComboboxOption,
  ComboboxOptionComponent,
  ComboboxOptionRenderProps,
  ComboboxOptionRenderer,
  ComboboxProps,
} from "./types";
import { SelectType, SelectionDisplayStyle } from "./types";
import { useCombobox } from "./useCombobox";

/**
 * 検索可能なドロップダウン(popover)選択コンポーネント
 *
 * 機能:
 * - 単一選択/複数選択
 * - 検索フィルタリング
 * - カスタマイズ可能なオプション表示
 * - 無限スクロール対応
 * - キーボード操作対応
 */
export default function Combobox<TOption extends ComboboxOption>(
  props: ComboboxProps<TOption>,
) {
  const { selectedOptions, onSelect, onClearAll, onKeyDown } =
    useCombobox(props);

  const isMultiSelect = props.type === SelectType.multiple;
  const multiSelectValues = isMultiSelect ? props.value : [];

  const shouldShowSelections =
    props.selectionDisplayStyle !== SelectionDisplayStyle.hidden;

  const getOptionLabel = useMemo(
    () => (value: string) => {
      const option = props.options.find((opt) => opt.value === value);
      return option?.label ?? props.valueToLabelCache?.get(value) ?? value;
    },
    [props.options, props.valueToLabelCache],
  );

  const filteredOptions = useMemo(() => {
    if (!props.search) return props.options;
    const searchLower = props.search.toLowerCase();
    return props.options.filter((option) =>
      option.label.toLowerCase().includes(searchLower),
    );
  }, [props.options, props.search]) satisfies readonly TOption[];

  const OptionContent = ({ option }: { option: TOption }) => {
    if (!props.renderOption) {
      return (
        <HStack className="items-center gap-2">
          {option.avatarUrl && (
            <Avatar className="size-6">
              <AvatarImage src={option.avatarUrl} />
            </Avatar>
          )}
          <Text variant="label" className="w-full text-wrapper">
            {option.label}
          </Text>
        </HStack>
      );
    }

    const renderProps = {
      option,
      isSelected: selectedOptions.has(option.value),
      showCheckbox: props.showCheckbox,
    } satisfies ComboboxOptionRenderProps<TOption>;

    const isReactComponent = (
      renderOption:
        | ComboboxOptionComponent<TOption>
        | ComboboxOptionRenderer<TOption>,
    ): renderOption is ComboboxOptionComponent<TOption> =>
      "$$typeof" in renderOption;

    if (isReactComponent(props.renderOption)) {
      const OptionComponent = props.renderOption;
      return <OptionComponent {...renderProps} />;
    }

    return props.renderOption(renderProps);
  };

  const SelectedValue = useMemo(() => {
    if (!shouldShowSelections) return null;

    if (isMultiSelect) {
      return multiSelectValues.map((value) => (
        <SelectedOptionChip
          key={value}
          value={value}
          label={getOptionLabel(value)}
          onSelect={onSelect}
        />
      ));
    }

    if (props.value) {
      return (
        <SelectedOptionChip
          key={props.value}
          value={props.value}
          label={getOptionLabel(props.value)}
          onSelect={onSelect}
        />
      );
    }
    return null;
  }, [
    isMultiSelect,
    props.value,
    multiSelectValues,
    getOptionLabel,
    onSelect,
    shouldShowSelections,
  ]);

  const ClearButton = isMultiSelect && shouldShowSelections && (
    <ActionButton
      icon="XOctagonIcon"
      onClick={onClearAll}
      variant="ghost"
      iconColor="active"
    />
  );

  const LoadingIndicator = props.isFetching && (
    <div className="flex justify-center py-2">
      <Loader />
    </div>
  );

  const lastOptionRef = useRef<string | null>(null);

  const NoResults = !props.isPending &&
    props.noResultsMessage &&
    filteredOptions.length === 0 && (
      <CommandEmpty>{props.noResultsMessage}</CommandEmpty>
    );

  const sentinel = useIntersectionObserver(() => {
    if (!props.isFetching && props.onScrollToEnd) {
      const lastOption = filteredOptions[filteredOptions.length - 1];
      if (lastOption) {
        const currentKey = lastOption?.key || lastOption.value;
        if (currentKey !== lastOptionRef.current) {
          lastOptionRef.current = currentKey;
          props.onScrollToEnd();
        }
      }
    }
  });

  const renderSearchInput = () => {
    if (!shouldShowSelections || !SelectedValue) {
      return (
        <div className="flex-1 py-2">
          <CommandInput
            noBorder
            className="outline-none disabled:cursor-not-allowed disabled:opacity-50 w-full"
            placeholder={props.searchPlaceholder}
            value={props.search}
            onValueChange={props.onSearchChange}
            onKeyDown={onKeyDown}
          />
        </div>
      );
    }

    // 複数選択の場合は常に縦レイアウトを使用
    if (isMultiSelect) {
      return (
        <VStack className="flex-1 gap-2 py-2 overflow-hidden">
          <div className="flex flex-wrap gap-1 w-full">{SelectedValue}</div>
          <div className="w-full">
            <CommandInput
              noBorder
              className="outline-none disabled:cursor-not-allowed disabled:opacity-50 w-full"
              placeholder={props.searchPlaceholder}
              value={props.search}
              onValueChange={props.onSearchChange}
              onKeyDown={onKeyDown}
            />
          </div>
        </VStack>
      );
    }

    // 単一選択の場合は、ラベルの長さを判断してレイアウトを変更
    // 選択済みのラベルを取得
    const selectedLabel = props.value ? getOptionLabel(props.value) : "";
    const isLongLabel = selectedLabel.length > 15;

    // 長いラベルの場合は縦レイアウトを使用
    if (isLongLabel) {
      return (
        <VStack className="flex-1 gap-2 py-2 overflow-hidden">
          <div className="flex flex-wrap gap-1 w-full">{SelectedValue}</div>
          <div className="w-full">
            <CommandInput
              noBorder
              className="outline-none disabled:cursor-not-allowed disabled:opacity-50 w-full"
              placeholder={props.searchPlaceholder}
              value={props.search}
              onValueChange={props.onSearchChange}
              onKeyDown={onKeyDown}
            />
          </div>
        </VStack>
      );
    }

    // 短いラベルの場合は横レイアウトを使用
    return (
      <HStack className="flex-1 items-center gap-2 py-2 overflow-hidden">
        <div className="flex-shrink-0">{SelectedValue}</div>
        <div className="flex-grow min-w-[120px]">
          <CommandInput
            noBorder
            className="outline-none disabled:cursor-not-allowed disabled:opacity-50 w-full"
            placeholder={props.searchPlaceholder}
            value={props.search}
            onValueChange={props.onSearchChange}
            onKeyDown={onKeyDown}
          />
        </div>
      </HStack>
    );
  };

  return (
    <Command shouldFilter={false} className="relative">
      {!props.hideSearch && (
        <div className="flex gap-2 border-b px-3 items-center">
          {renderSearchInput()}
          {ClearButton}
        </div>
      )}
      <CommandList
        className={cn("gap-1 p-1", props.isPlaceholderData && "opacity-50")}
      >
        {NoResults}
        {filteredOptions.map((option: TOption) => (
          <CommandItem
            key={option.value}
            value={String(option.value)}
            onSelect={onSelect}
            disabled={option.disabled}
            className="cursor-pointer items-center gap-2"
          >
            {props.showCheckbox && (
              <Checkbox
                checked={selectedOptions.has(option.value)}
                size={16}
                disabled={option.disabled}
                readOnly
                className="pointer-events-none"
              />
            )}
            <OptionContent option={option} />
          </CommandItem>
        ))}
        {LoadingIndicator}
        {props.onScrollToEnd && (
          <div ref={sentinel} role="presentation" aria-hidden="true" />
        )}
      </CommandList>
    </Command>
  );
}
